import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import qs from 'qs';
import { FC, Fragment, useEffect } from 'react';

import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/myCustomers/myCustomers.utils';
import { useCurrentAgent } from '@/hooks/agents/useCurrentAgent';
import { PAGE_VIEW_TITLE } from '@/utils/gtm';
import { QS_ARRAY_PARAMS } from '@/utils/helpers';
import { EVENT_NAME, trackEventOnMixpanel } from '@/utils/mixPanel';
import { generateButtonId } from '@/utils/tracking';
import { Button, useSmartphoneDevice } from '@proprioo/salatim';

import { LeadViewTab } from '../../interfaces';
import { ButtonWrapper, NoOpportunitiesLayout } from './ExternalLeads.styles';
import ExternalLeadsDesktop from './externalLeadsDesktop/ExternalLeadsDesktop';
import ExternalLeadsMobile from './externalLeadsMobile/ExternalLeadsMobile';
import { ExternalLeadsProps } from './interfaces';
import { getSection, getTabUrl } from '../../LeadView.utils';
import {
  getActiveFilters,
  getSectionFilters
} from '../filters/LeadFilters.utils';
import { leadViewSelector } from '@/components/state/LeadViewState';
import { useRecoilValue } from 'recoil';

const ExternalLeads: FC<ExternalLeadsProps> = props => {
  const { t } = useTranslation();
  const { asPath, query } = useRouter();
  const page = Number(query.page) | 1;

  const isSmartphone = useSmartphoneDevice();
  const currentAgent = useCurrentAgent();

  const params = qs.stringify(
    { agentIds: [currentAgent?.id] },
    QS_ARRAY_PARAMS
  );

  const { currentTab, isLoading, opportunities } = props;
  const nbOfOpportunities = opportunities.length;

  // this is the size of the oppurtunities requested by the backend call such as --> src/pages/api/external-lead/archived-leads.ts
  const OPPORTUNITIES_SIZE = 20;
  const areThereOtherPages = nbOfOpportunities >= OPPORTUNITIES_SIZE;

  const { leadViewFilters } = useRecoilValue(leadViewSelector);
  const section = getSection(currentTab);
  const sectionFilters = getActiveFilters(
    getSectionFilters(section, leadViewFilters)
  );

  useEffect(() => {
    if (currentTab === LeadViewTab.LEADS_ALL_EXTERNAL) {
      const desktopImpressions =
        nbOfOpportunities > DEFAULT_TABLE_PAGE_SIZE
          ? DEFAULT_TABLE_PAGE_SIZE
          : nbOfOpportunities;

      trackEventOnMixpanel(EVENT_NAME.OUTBOUND_PROSP_SEARCH, {
        impressions: isSmartphone ? nbOfOpportunities : desktopImpressions,
        pagePage: asPath,
        pageTitle: PAGE_VIEW_TITLE.LEADS
      });
    }
  }, [currentTab, nbOfOpportunities]);

  if (!nbOfOpportunities && !isLoading) {
    const thereAreNoMorePagesForExpiredLeads =
      page > 1 && currentTab === LeadViewTab.LEADS_ALL_EXTERNAL_EXPIRED;
    // redirecting to first page if there are no more pages.
    // This is needed since the pagination handling is a mixture of both server and client
    return (
      <NoOpportunitiesLayout>
        <div>
          {t(
            thereAreNoMorePagesForExpiredLeads
              ? 'thereAreNoMorePages'
              : 'noExternalLeadsConsultBusinessStockExchange'
          )}
        </div>
        {currentAgent?.id && (
          <ButtonWrapper>
            <Link
              href={
                thereAreNoMorePagesForExpiredLeads
                  ? getTabUrl(currentTab, 1, props.agentIds, sectionFilters)
                  : `/business-stock-exchange${params}`
              }
              id={
                thereAreNoMorePagesForExpiredLeads
                  ? 'goBackToFirstPage'
                  : generateButtonId(
                      PAGE_VIEW_TITLE.LEADS_EXTERNAL,
                      'goToBusinessStockExchange'
                    )
              }
            >
              <Button
                label={t(
                  thereAreNoMorePagesForExpiredLeads
                    ? 'goBackToFirstPage'
                    : 'businessStockExchange'
                )}
              />
            </Link>
          </ButtonWrapper>
        )}
      </NoOpportunitiesLayout>
    );
  }

  return (
    <Fragment>
      {isSmartphone ? (
        <ExternalLeadsMobile
          {...props}
          areThereOtherPages={areThereOtherPages}
        />
      ) : (
        <ExternalLeadsDesktop
          {...props}
          areThereOtherPages={areThereOtherPages}
        />
      )}
    </Fragment>
  );
};

export default ExternalLeads;
